import { useState } from 'react';
import { SCRIPTS, ASSET_CATEGORIES } from '@/data/mockData';
import { useAppContext } from '@/context/AppContext';
import { Copy, Check, Star, MessageSquareQuote, CheckCircle2 } from 'lucide-react';
import type { AssetCategory } from '@/types';

export function Scripts() {
  const { savedScriptIds, toggleSavedScript } = useAppContext();
  const [activeCategory, setActiveCategory] = useState<AssetCategory | '全部'>('全部');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filteredScripts = activeCategory === '全部' 
    ? SCRIPTS 
    : SCRIPTS.filter(item => item.category === activeCategory);

  const handleCopy = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };

  return (
    <div className="flex-1 w-full max-w-5xl mx-auto py-6 px-4 md:px-8 space-y-6 animate-in fade-in duration-500 pb-24 md:pb-8">
      {/* Header & Filters */}
      <div className="space-y-5 sticky top-0 bg-background/95 backdrop-blur-md pt-2 pb-4 z-20 border-b">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">营销话术库</h1>
        
        {/* Horizontal Scrollable Filter Pills */}
        <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar gap-2">
          <button
            onClick={() => setActiveCategory('全部')}
            data-testid="filter-script-全部"
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              activeCategory === '全部'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            全部
          </button>
          {ASSET_CATEGORIES.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              data-testid={`filter-script-${category}`}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                activeCategory === category
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Script Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredScripts.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            暂无该分类的话术
          </div>
        ) : (
          filteredScripts.map(script => {
            const isSaved = savedScriptIds.includes(script.id);
            const isCopied = copiedId === script.id;

            return (
              <div 
                key={script.id}
                className="bg-card rounded-xl border border-border/60 shadow-sm flex flex-col overflow-hidden hover:shadow-md transition-shadow group"
                data-testid={`script-card-${script.id}`}
              >
                {/* Card Header */}
                <div className="p-5 border-b border-border/40 bg-muted/20 relative">
                  <div className="flex justify-between items-start gap-4">
                    <div className="space-y-1">
                      <span className="inline-block px-2 py-0.5 bg-secondary text-secondary-foreground text-[10px] font-bold rounded mb-1">
                        {script.category}
                      </span>
                      <h2 className="font-bold text-lg leading-tight text-foreground group-hover:text-primary transition-colors">
                        {script.title}
                      </h2>
                    </div>
                    <button
                      onClick={() => toggleSavedScript(script.id)}
                      className="p-2 -mr-2 -mt-2 rounded-full hover:bg-secondary text-muted-foreground transition-colors"
                      data-testid={`btn-save-script-${script.id}`}
                      aria-label="收藏话术"
                    >
                      {isSaved ? (
                        <Star size={20} className="text-accent fill-accent" />
                      ) : (
                        <Star size={20} />
                      )}
                    </button>
                  </div>
                  
                  <div className="mt-4 flex gap-2 text-sm text-muted-foreground bg-background rounded-md p-3 border border-border/50">
                    <MessageSquareQuote size={16} className="flex-shrink-0 mt-0.5 text-primary/60" />
                    <span className="leading-snug">{script.scenario}</span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="text-sm md:text-base leading-relaxed text-foreground/90 whitespace-pre-wrap flex-1 font-serif">
                    {script.script}
                  </div>
                  
                  <div className="mt-6 pt-5 border-t border-border/40">
                    <div className="text-xs font-bold text-muted-foreground mb-3 uppercase tracking-wider">沟通要点</div>
                    <ul className="space-y-2">
                      {script.keyPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                          <CheckCircle2 size={16} className="text-primary/70 flex-shrink-0 mt-0.5" />
                          <span className="leading-snug">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="p-4 bg-muted/10 border-t border-border/40 flex justify-end">
                  <button
                    onClick={() => handleCopy(script.id, script.script)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      isCopied 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                    data-testid={`btn-copy-script-${script.id}`}
                  >
                    {isCopied ? (
                      <>
                        <Check size={16} />
                        <span>已复制</span>
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        <span>复制话术</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
